import { CircleAlert, CircleCheck, MailPlus, RefreshCw, X } from 'lucide-react'
import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import type { ApiInvite } from '@/features/users/types'
import {
  selectProjectInviteItems,
  selectProjectInviteRowErrors,
  selectProjectInvitesBlocked,
  selectProjectInvitesCache,
  selectProjectInvitesError,
} from '@/features/users/usersSelectors'
import {
  fetchProjectInvites,
  projectInvitesClosed,
  projectInvitesRetried,
  resendProjectInvite,
  revokeProjectInvite,
} from '@/features/users/usersSlice'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'

const columnHeadClass =
  'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

/** Below Tailwind's `sm` (40rem): the table becomes a stacked list. */
const PHONE_QUERY = '(max-width: 39.99rem)'

type RowAction = 'resend' | 'revoke'

interface PendingInvitesProps {
  orgId: string
  projectId: string
  projectName: string
  /** Where focus goes after the last row is revoked. */
  inviteTriggerRef: RefObject<HTMLButtonElement | null>
}

/**
 * A project's pending invitations (TASK-7F3EE34E D2), inside the members panel.
 * The panel's caller gates who sees it (org admin + project WRITE); a stale
 * permission still lands in the forbidden state, never a silent empty.
 *
 * Resend rotates the link (the answer is a new row, so rows are keyed by
 * address); Revoke confirms inline under its row. A row-scoped refusal renders
 * under that address and the slice reloads the list.
 */
export function ProjectPendingInvites({
  orgId,
  projectId,
  projectName,
  inviteTriggerRef,
}: PendingInvitesProps) {
  const { t, i18n } = useTranslation(['projectWorkspace', 'peopleAdmin'])
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()
  const isPhone = useMediaQuery(PHONE_QUERY)

  const items = useAppSelector((state) =>
    selectProjectInviteItems(state, projectId)
  )
  const cache = useAppSelector(selectProjectInvitesCache)
  const error = useAppSelector((state) =>
    selectProjectInvitesError(state, projectId)
  )
  const blocked = useAppSelector((state) =>
    selectProjectInvitesBlocked(state, projectId)
  )
  const rowErrors = useAppSelector(selectProjectInviteRowErrors)

  const [confirming, setConfirming] = useState<string | null>(null)
  const [busy, setBusy] = useState<{ email: string; action: RowAction } | null>(
    null
  )
  const [status, setStatus] = useState<{
    kind: 'resent' | 'revoked'
    email: string
  } | null>(null)
  const resendRefs = useRef(new Map<string, HTMLButtonElement>())
  const revokeRefs = useRef(new Map<string, HTMLButtonElement>())
  const keepRef = useRef<HTMLButtonElement | null>(null)

  // A panel owns its load. Opening (or another project) loads; afterwards only
  // a stale mark (a send, a row refusal, Retry) reloads — a FAILED load flips
  // `invalidated` off, and that edge must not retry by itself. The thunk's
  // condition dedupes the double dispatch on mount.
  const invalidated = cache.invalidated
  useEffect(() => {
    void dispatch(fetchProjectInvites(projectId))
  }, [dispatch, projectId])
  useEffect(() => {
    if (invalidated) void dispatch(fetchProjectInvites(projectId))
  }, [dispatch, projectId, invalidated])

  useEffect(() => {
    return () => {
      dispatch(projectInvitesClosed())
    }
  }, [dispatch])

  useEffect(() => {
    if (confirming !== null) keepRef.current?.focus()
  }, [confirming])

  const language = i18n.resolvedLanguage
  const roleLabel = (role: string | undefined): string =>
    role === undefined || role === ''
      ? '—'
      : t(`peopleAdmin:roles.${role.toLowerCase()}`, { defaultValue: role })

  /** The access an invite grants, as a pill (a row without a level reads as WRITE). */
  const accessPill = (row: ApiInvite) => (
    <StatusPill
      tone={row.projectAccessLevel === 'READ' ? 'neutral' : 'proposed'}
    >
      {t(`members.access.${row.projectAccessLevel ?? 'WRITE'}`)}
    </StatusPill>
  )

  const onRetryClicked = (): void => {
    dispatch(projectInvitesRetried(projectId))
  }

  const onResendClicked = async (row: ApiInvite): Promise<void> => {
    if (busy !== null) return
    setConfirming(null)
    setStatus(null)
    setBusy({ email: row.email, action: 'resend' })
    const action = await dispatch(
      resendProjectInvite({ projectId, inviteId: row.id, email: row.email })
    )
    setBusy(null)
    if (resendProjectInvite.fulfilled.match(action))
      setStatus({ kind: 'resent', email: row.email })
    resendRefs.current.get(row.email.toLowerCase())?.focus()
  }

  const onRevokeClicked = (row: ApiInvite): void => {
    if (busy !== null) return
    setStatus(null)
    setConfirming((current) => (current === row.id ? null : row.id))
  }

  const onKeepClicked = (row: ApiInvite): void => {
    setConfirming(null)
    revokeRefs.current.get(row.id)?.focus()
  }

  const onConfirmKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    row: ApiInvite
  ): void => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      onKeepClicked(row)
    }
  }

  const onRevokeConfirmed = async (row: ApiInvite): Promise<void> => {
    if (busy !== null) return
    const index = items.findIndex((candidate) => candidate.id === row.id)
    const neighbour = items[index + 1] ?? items[index - 1]
    setConfirming(null)
    setBusy({ email: row.email, action: 'revoke' })
    const action = await dispatch(
      revokeProjectInvite({ projectId, inviteId: row.id, email: row.email })
    )
    setBusy(null)
    if (!revokeProjectInvite.fulfilled.match(action)) return
    setStatus({ kind: 'revoked', email: row.email })
    const next =
      neighbour === undefined
        ? undefined
        : resendRefs.current.get(neighbour.email.toLowerCase())
    if (next !== undefined) next.focus()
    else inviteTriggerRef.current?.focus()
  }

  const rowError = (row: ApiInvite): string | null => {
    const refusal = rowErrors[row.email.toLowerCase()]
    return refusal === undefined ? null : toMessage(refusal)
  }

  const renderActions = (row: ApiInvite, phone: boolean) => {
    const rowBusy = busy?.email === row.email
    const resending = rowBusy && busy?.action === 'resend'
    const revoking = rowBusy && busy?.action === 'revoke'
    const size = phone ? 'h-[44px] flex-1' : 'h-7 px-2.5 text-xs'
    return (
      <span className={phone ? 'mt-1 flex gap-2' : 'flex justify-end gap-2'}>
        <Button
          type="button"
          variant="outline"
          data-testid="project-invite-resend"
          aria-label={t('members.invites.resendLabel', { email: row.email })}
          aria-busy={resending || undefined}
          aria-disabled={resending || undefined}
          disabled={revoking}
          onClick={() => void onResendClicked(row)}
          className={size}
          ref={registerRef(resendRefs, row.email.toLowerCase())}
        >
          <RefreshCw
            aria-hidden
            className={
              resending ? 'size-3.5 motion-safe:animate-spin' : 'size-3.5'
            }
          />
          {resending
            ? t('members.invites.resending')
            : t('members.invites.resend')}
        </Button>
        <Button
          type="button"
          variant="outline"
          data-testid="project-invite-revoke"
          aria-label={t('members.invites.revokeLabel', { email: row.email })}
          aria-expanded={confirming === row.id}
          aria-controls={`revoke-confirm-${row.id}`}
          aria-busy={revoking || undefined}
          aria-disabled={revoking || undefined}
          disabled={resending}
          onClick={() => onRevokeClicked(row)}
          className={cn(size, 'text-blocked')}
          ref={registerRef(revokeRefs, row.id)}
        >
          <X aria-hidden className="size-3.5" />
          {revoking
            ? t('members.invites.revoking')
            : t('members.invites.revoke')}
        </Button>
      </span>
    )
  }

  const renderConfirm = (row: ApiInvite, phone: boolean) => (
    <div
      id={`revoke-confirm-${row.id}`}
      role="group"
      aria-label={t('members.invites.revokeConfirm.groupLabel')}
      data-testid="project-invite-revoke-confirm"
      onKeyDown={(event) => onConfirmKeyDown(event, row)}
      className={
        phone
          ? 'mt-2 grid gap-2'
          : 'flex flex-wrap items-center justify-between gap-2'
      }
    >
      <span className="text-sm text-foreground">
        {phone
          ? t('members.invites.revokeConfirm.bodyShort')
          : t('members.invites.revokeConfirm.body', { email: row.email })}
      </span>
      <span className={phone ? 'flex flex-col-reverse gap-2' : 'flex gap-2'}>
        <Button
          type="button"
          variant="outline"
          onClick={() => onKeepClicked(row)}
          className={phone ? 'h-[44px]' : 'h-7 px-2.5 text-xs'}
          ref={keepRef}
        >
          {t('members.invites.revokeConfirm.cancel')}
        </Button>
        <Button
          type="button"
          data-testid="project-invite-revoke-confirm-submit"
          onClick={() => void onRevokeConfirmed(row)}
          className={cn(
            'bg-destructive text-destructive-foreground hover:bg-destructive/90',
            phone ? 'h-[44px]' : 'h-7 px-2.5 text-xs'
          )}
        >
          {t('members.invites.revokeConfirm.confirm')}
        </Button>
      </span>
    </div>
  )

  const showLoading =
    items.length === 0 &&
    error === null &&
    blocked === null &&
    (cache.loading || cache.invalidated)

  return (
    <section
      aria-labelledby={`pending-invites-${projectId}`}
      data-testid="project-pending-invites"
      className="mt-3 border-t border-border pt-3"
    >
      <div className="flex items-center gap-2">
        <h3 id={`pending-invites-${projectId}`}>
          <SectionLabel>{t('members.invites.title')}</SectionLabel>
        </h3>
        {items.length > 0 && (
          <StatusPill tone="neutral">
            <span
              aria-label={t('members.invites.countLabel', {
                count: items.length,
              })}
            >
              {items.length}
            </span>
          </StatusPill>
        )}
      </div>

      {status !== null && (
        <p
          role="status"
          data-testid="project-invite-status"
          className="mt-2 flex items-start gap-1.5 text-sm text-done"
        >
          <CircleCheck aria-hidden className="mt-0.5 size-4 shrink-0" />
          <span className="min-w-0 break-words">
            {t(`members.invites.${status.kind}`, { email: status.email })}
          </span>
        </p>
      )}

      <div className="mt-2">
        {blocked !== null ? (
          <ForbiddenPanel
            requested={`/${orgId}/projects/${projectId}/members/invites`}
          />
        ) : error !== null ? (
          <ErrorPanel error={error} onRetryClicked={onRetryClicked} />
        ) : showLoading ? (
          <LoadingPanel rows={2} label={t('members.invites.loading')} />
        ) : items.length === 0 ? (
          <EmptyPanel
            icon={MailPlus}
            className="py-8"
            title={t('members.invites.empty.title')}
            body={t('members.invites.empty.body')}
          />
        ) : isPhone ? (
          <ul>
            {items.map((row) => {
              const refusal = rowError(row)
              return (
                <li
                  key={row.email.toLowerCase()}
                  data-testid="project-invite-row"
                  aria-busy={busy?.email === row.email || undefined}
                  className="border-b border-border py-2.5 last:border-b-0"
                >
                  <span className="block break-all text-sm font-medium text-foreground">
                    {row.email}
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-1.5">
                    {accessPill(row)}
                    <span className="text-xs text-muted-foreground">
                      {roleLabel(row.role)}
                    </span>
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {t('members.invites.itemInviter', {
                      name: row.inviterName ?? '—',
                    })}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {t('members.invites.itemDates', {
                      sent: formatDate(row.createdAt, language) ?? '—',
                      expires: formatDate(row.expiresAt, language) ?? '—',
                    })}
                  </span>
                  {refusal !== null && <RowRefusal message={refusal} />}
                  {renderActions(row, true)}
                  {confirming === row.id && renderConfirm(row, true)}
                </li>
              )
            })}
          </ul>
        ) : (
          <Table>
            <TableCaption className="sr-only">
              {t('members.invites.caption', { project: projectName })}
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead className={columnHeadClass}>
                  {t('members.invites.columns.email')}
                </TableHead>
                <TableHead className={columnHeadClass}>
                  {t('members.invites.columns.access')}
                </TableHead>
                <TableHead className={columnHeadClass}>
                  {t('members.invites.columns.role')}
                </TableHead>
                <TableHead className={columnHeadClass}>
                  {t('members.invites.columns.inviter')}
                </TableHead>
                <TableHead className={columnHeadClass}>
                  {t('members.invites.columns.sent')}
                </TableHead>
                <TableHead className={columnHeadClass}>
                  {t('members.invites.columns.expires')}
                </TableHead>
                <TableHead>
                  <span className="sr-only">
                    {t('members.invites.columns.actions')}
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => {
                const refusal = rowError(row)
                return (
                  <Fragment key={row.email.toLowerCase()}>
                    <TableRow
                      data-testid="project-invite-row"
                      aria-busy={busy?.email === row.email || undefined}
                    >
                      <TableCell className="py-2.5 font-medium text-foreground">
                        {row.email}
                        {refusal !== null && <RowRefusal message={refusal} />}
                      </TableCell>
                      <TableCell>{accessPill(row)}</TableCell>
                      <TableCell className="text-sm text-ink-soft">
                        {roleLabel(row.role)}
                      </TableCell>
                      <TableCell className="text-sm text-ink-soft">
                        {row.inviterName ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm tabular-nums text-ink-soft">
                        {formatDate(row.createdAt, language) ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm tabular-nums text-foreground">
                        {formatDate(row.expiresAt, language) ?? '—'}
                      </TableCell>
                      <TableCell>{renderActions(row, false)}</TableCell>
                    </TableRow>
                    {confirming === row.id && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          {renderConfirm(row, false)}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  )
}

function RowRefusal({ message }: { message: string }) {
  return (
    <p
      role="alert"
      className="mt-1 flex items-start gap-1.5 text-xs font-normal text-blocked"
    >
      <CircleAlert aria-hidden className="mt-px size-3.5 shrink-0" />
      <span className="min-w-0 break-words">{message}</span>
    </p>
  )
}

/**
 * A ref callback that records its element in a keyed map and forgets it on
 * unmount — how the list moves focus to a sibling row's button. The map is only
 * touched inside the callback, never during render.
 */
function registerRef(
  map: RefObject<Map<string, HTMLButtonElement>>,
  key: string
): (node: HTMLButtonElement | null) => void {
  return (node) => {
    if (node === null) map.current.delete(key)
    else map.current.set(key, node)
  }
}
